<template>
    <div v-bind="attrs" ref="root" class="flex-item" :class="{ 'select-mode': asSelect }">
        <div v-if="selectedProduct" class="font-weight-bold product-box" :class="{ 'selected-product': selectedProduct }">
            <ImageLoader v-if="selectedProduct.image" class="d-block border product-image" :src="selectedProduct.image" alt="" />
            {{ selectedProduct.name }}
        </div>

        <div class="flex-item">
            <b-form-input
                :id="$attrs.id"
                v-model="searchText"
                :placeholder="$t('generator.productSearchPlaceholder')"
                autocomplete="off"
                @input="onChangeText"
                @focus="onFocusInput"
                @keydown.esc="closeDropdown"
            />

            <div v-if="loading && !asSelect" class="d-flex justify-content-center">
                <b-spinner />
            </div>

            <error-message v-else-if="error" v-model="error" />

            <div v-else-if="items.length && !asSelect" class="product-box-wrapper">
                <div v-for="item in items" :key="item.id" class="product-box" @click="onSelectItem(item)">
                    <ImageLoader v-if="item.image" class="d-block border product-image" :src="item.image" alt="" />
                    {{ item.name }}
                </div>
            </div>

            <div v-else-if="asSelect && ((items.length && showDropdown) || loading)" class="product-box-dropdown" role="listbox">
                <div v-if="loading" class="d-flex justify-content-center p-4">
                    <b-spinner />
                </div>

                <div v-for="item in items" :key="item.id" class="product-box" @click="onSelectItem(item)">
                    <ImageLoader v-if="item.image" class="d-block border product-image" :src="item.image" alt="" />
                    {{ item.name }}
                </div>
            </div>

            <div v-else-if="response && !items.length" class="d-flex justify-content-center">
                {{ $t('noResult') }}
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import { debounce } from 'lodash';
    import pluginService from '@/services/pluginService';
    import ErrorMessage from '@/components/common/ErrorMessage.vue';
    import ImageLoader from '@/components/common/ImageLoader.vue';

    export default {
        name: 'ProductSearch',
        inheritAttrs: false,
        components: {
            ErrorMessage,
            ImageLoader,
        },
        props: {
            value: {
                type: Object,
            },
            asSelect: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                searchText: '',
                loading: false,
                error: null,
                response: null,
                showDropdown: false,
            };
        },
        computed: {
            attrs() {
                return { ...this.$attrs, id: undefined };
            },
            items() {
                return this.response || [];
            },
            selectedProduct: {
                get() {
                    return this.value;
                },
                set(value) {
                    this.$emit('input', value);
                },
            },
            ...mapState({
                userId: state => state.auth.user.id,
                currentSpaceId: state => state.space.currentSpaceId,
            }),
        },
        methods: {
            reset() {
                this.loading = false;
                this.error = null;
                this.response = null;
                if (this.asSelect) this.showDropdown = false;
            },
            async searchProducts() {
                this.loading = true;
                try {
                    this.response = await pluginService.searchProducts(this.userId, this.searchText);
                } catch (error) {
                    this.error = error;
                } finally {
                    this.loading = false;
                    if (this.asSelect) this.showDropdown = !!(this.response && this.response.length);
                }
            },
            onFocusInput() {
                if (this.asSelect && this.items.length) {
                    this.showDropdown = true;
                }
            },
            closeDropdown() {
                if (this.asSelect) this.showDropdown = false;
            },
            onChangeText: debounce(function debouncedOnChangeText(text) {
                if (text) {
                    this.searchProducts();
                    this.selectedProduct = undefined;
                    if (this.asSelect) this.showDropdown = true;
                } else {
                    this.reset();
                }
            }, 750),
            onSelectItem(product) {
                this.selectedProduct = product;
                this.searchText = '';
                if (this.asSelect) this.showDropdown = false;
                this.reset();
            },
            handleClickOutside(e) {
                if (!this.asSelect) return;
                const root = this.$refs.root;
                if (root && !root.contains(e.target)) {
                    this.showDropdown = false;
                }
            },
        },
        mounted() {
            document.addEventListener('click', this.handleClickOutside, true);
        },
        destroyed() {
            document.removeEventListener('click', this.handleClickOutside, true);
        },
    };
</script>

<style lang="scss" scoped>
    .flex-item {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .product-box-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        max-height: 300px;
        overflow-y: auto;
    }

    .product-box {
        display: flex;
        align-items: center;

        &:not(.selected-product) {
            padding: 1rem;
            cursor: pointer;

            &:hover {
                background: $gray-200;
            }
        }
    }

    .product-image {
        margin-right: 15px;
        max-width: 100px;
        max-height: 60px;

        &.image-error,
        &.image-loader {
            width: 40px;
            height: 40px;
            background-size: 90%;
        }
    }

    .select-mode {
        position: relative;
    }

    .product-box-dropdown {
        position: absolute;
        top: calc(100% + 0.25rem);
        left: 0;
        right: 0;
        z-index: 1050; /* above forms */
        background: #fff;
        border: 1px solid $gray-300;
        border-radius: 0.25rem;
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        max-height: 300px;
        overflow-y: auto;
        padding: 0.25rem 0;
    }
</style>
